import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, MessageCircle } from "lucide-react";
import { listChats } from "@/lib/symptom-chat.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export const Route = createFileRoute("/_authenticated/admin/chats")({
  component: ChatsPage,
});

const fmt = (d: string) => new Date(d).toLocaleString("en-ZA", { timeZone: "Africa/Johannesburg", dateStyle: "medium", timeStyle: "short" });
const waLink = (phone: string) => {
  const d = phone.replace(/\D/g, "");
  return `https://wa.me/${d.startsWith("0") ? "27" + d.slice(1) : d}`;
};

function ChatsPage() {
  const fn = useServerFn(listChats);
  const { data, isPending, isError, error } = useQuery({ queryKey: ["admin", "chats"], queryFn: () => fn() });
  const [open, setOpen] = useState<string | null>(null);

  if (isPending) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (isError) return <p className="text-sm text-destructive">Failed to load: {(error as Error).message}</p>;

  return (
    <div className="space-y-4">
      <h1 className="font-display text-2xl font-bold text-primary">Website chats</h1>
      {data.length === 0 && <p className="text-muted-foreground">No chats yet.</p>}
      {data.map((c) => {
        const userCount = c.messages.filter((m) => m.role === "user").length;
        return (
          <Card key={c.id}>
            <CardHeader className="cursor-pointer" onClick={() => setOpen(open === c.id ? null : c.id)}>
              <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-base">
                <span>{c.patient_name} · <a href={waLink(c.patient_phone)} target="_blank" rel="noreferrer" className="text-primary underline" onClick={(e) => e.stopPropagation()}>{c.patient_phone}</a></span>
                <span className="flex items-center gap-2 text-xs font-normal text-muted-foreground">
                  {c.whatsapp_clicked && <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-primary"><MessageCircle className="h-3 w-3" /> Wanted WhatsApp</span>}
                  {userCount} messages · {fmt(c.created_at)}
                </span>
              </CardTitle>
            </CardHeader>
            {open === c.id && (
              <CardContent className="space-y-3">
                {c.messages.map((m, i) => (
                  <div key={i} className={m.role === "user" ? "ml-auto max-w-[80%] rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground whitespace-pre-wrap" : "max-w-[80%] text-sm whitespace-pre-wrap"}>
                    {m.content}
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
}
